import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
    Select,
    Button,
    Input,
    Modal,
    Spin,
    Typography,
    Collapse,
    Tag,
    Space,
    Tooltip,
    Tabs,
    Timeline,
    Empty,
    Badge,
    Popover,
    Radio,
} from 'antd';
import {
    PlusOutlined,
    SaveOutlined,
    CloudUploadOutlined,
    FileTextOutlined,
    BookOutlined,
    ToolOutlined,
    ThunderboltOutlined,
    UserOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    HistoryOutlined,
    DiffOutlined,
    EyeOutlined,
    UndoOutlined,
    CodeOutlined,
    FormOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import yaml from 'js-yaml';
import type { PromptCategory, FileEntry } from './prompts/types';
import type { SkillStudioPromptsSectionProps } from './SkillStudio.types';
import { SimpleMarkdown, renderValue } from './prompts/SimpleMarkdown';
import { StructuredForm, CATEGORY_TEMPLATES, deepMergeDefaults } from './prompts/StructuredForm';
import { DiffView, LiveDiffView } from './prompts/DiffViews';
import type { DiffHunk } from './prompts/DiffViews';

const { Text, Title } = Typography;
const { TextArea } = Input;

function serializeYaml(obj: unknown): string {
    return yaml.dump(obj, { lineWidth: -1, quotingType: '"', forceQuotes: false });
}

function stripUnsupportedPromptFields(obj: Record<string, unknown>): Record<string, unknown> {
    const result = { ...obj };
    delete result.customer_access;
    delete result.customerAccess;
    return result;
}

const CATEGORY_META: Record<PromptCategory, { label: string; icon: React.ReactNode }> = {
    knowledge: { label: 'Knowledge', icon: <BookOutlined /> },
    tasks: { label: 'Tasks', icon: <ToolOutlined /> },
    skills: { label: 'Skills', icon: <ThunderboltOutlined /> },
    personas: { label: 'Personas', icon: <UserOutlined /> },
};

const CATEGORY_ORDER: PromptCategory[] = ['knowledge', 'tasks', 'skills', 'personas'];

const SkillStudioPromptsSection: React.FC<SkillStudioPromptsSectionProps> = ({
    tenants,
    tenantsLoading,
    selectedTenant,
    onSelectTenant,
    onCreateTenant,
    branchNamePrefix = '',
    draftBranch,
    contentTree,
    contentLoading,
    selectedFile,
    fileContent,
    fileLoading,
    onSelectFile,
    editorValue,
    onEditorValueChange,
    isDirty,
    saving,
    onSave,
    publishing,
    onPublish,
    onCloseDraft,
    onCreateFile,
    onDeleteFile,
    history,
    historyLoading,
    onLoadHistory,
    diffData,
    diffLoading,
    onLoadDiff,
    blameData,
    blameLoading,
    onLoadBlame,
    versionDiff,
    versionDiffLoading,
    onLoadVersionDiff,
    savedContent,
    onRevert,
}) => {

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newTenantName, setNewTenantName] = useState('');
    const [fromBranch, setFromBranch] = useState<string | undefined>(undefined);

    const [newFileModalOpen, setNewFileModalOpen] = useState(false);
    const [newFileCategory, setNewFileCategory] = useState<PromptCategory>('tasks');
    const [newFileId, setNewFileId] = useState('');
    const [newFileFormValues, setNewFileFormValues] = useState<Record<string, unknown> | null>(null);

    const [savePopoverOpen, setSavePopoverOpen] = useState(false);
    const [commitMsg, setCommitMsg] = useState('');
    const [fileSearchText, setFileSearchText] = useState('');
    const [compareFrom, setCompareFrom] = useState<string | null>(null);
    const [compareTo, setCompareTo] = useState<string | null>(null);

    const [editorMode, setEditorMode] = useState<'form' | 'raw'>('form');
    const [formValues, setFormValues] = useState<Record<string, unknown> | null>(null);
    const formValuesRef = useRef<Record<string, unknown> | null>(null);
    const trimmedNewBranchName = newTenantName.trim();
    const branchNamePreview = trimmedNewBranchName ? `${branchNamePrefix}${trimmedNewBranchName}` : '';

    const editorScrollRef = useRef<HTMLDivElement>(null);
    const previewScrollRef = useRef<HTMLDivElement>(null);
    const isSyncingScroll = useRef(false);

    const skillOptions = useMemo(
        () => (contentTree?.skills ?? []).map((entry) => ({
            value: entry.file_id,
            label: entry.filename.replace(/\.ya?ml$/i, '') || entry.file_id,
        })),
        [contentTree],
    );

    const handleEditorScroll = useCallback(() => {
        if (isSyncingScroll.current) return;
        const el = editorScrollRef.current;
        const target = previewScrollRef.current;
        if (!el || !target) return;
        const maxScroll = el.scrollHeight - el.clientHeight;
        if (maxScroll <= 0) return;
        const pct = el.scrollTop / maxScroll;
        isSyncingScroll.current = true;
        target.scrollTop = pct * (target.scrollHeight - target.clientHeight);
        requestAnimationFrame(() => { isSyncingScroll.current = false; });
    }, []);

    const handlePreviewScroll = useCallback(() => {
        if (isSyncingScroll.current) return;
        const el = previewScrollRef.current;
        const target = editorScrollRef.current;
        if (!el || !target) return;
        const maxScroll = el.scrollHeight - el.clientHeight;
        if (maxScroll <= 0) return;
        const pct = el.scrollTop / maxScroll;
        isSyncingScroll.current = true;
        const textarea = target.querySelector('textarea');
        if (textarea && textarea.scrollHeight > textarea.clientHeight) {
            textarea.scrollTop = pct * (textarea.scrollHeight - textarea.clientHeight);
        } else {
            target.scrollTop = pct * (target.scrollHeight - target.clientHeight);
        }
        requestAnimationFrame(() => { isSyncingScroll.current = false; });
    }, []);

    // Attach scroll listener directly to the textarea element (raw mode)
    // so scroll sync works even when the wrapper div itself doesn't scroll.
    useEffect(() => {
        const wrapper = editorScrollRef.current;
        if (!wrapper) return;
        const textarea = wrapper.querySelector('textarea');
        if (!textarea) return;
        const onTextareaScroll = () => {
            if (isSyncingScroll.current) return;
            const maxScroll = textarea.scrollHeight - textarea.clientHeight;
            if (maxScroll <= 0) return;
            const pct = textarea.scrollTop / maxScroll;
            const target = previewScrollRef.current;
            if (!target) return;
            isSyncingScroll.current = true;
            target.scrollTop = pct * (target.scrollHeight - target.clientHeight);
            requestAnimationFrame(() => { isSyncingScroll.current = false; });
        };
        textarea.addEventListener('scroll', onTextareaScroll);
        return () => textarea.removeEventListener('scroll', onTextareaScroll);
    }, [editorMode, selectedFile, fileContent]);

    useEffect(() => {
        if (fileContent && selectedFile) {
            const loaded = JSON.parse(JSON.stringify(fileContent.content));
            const tpl = CATEGORY_TEMPLATES[selectedFile.category];
            const initial = Object.keys(tpl).length > 0
                ? deepMergeDefaults(tpl, loaded)
                : loaded;
            const sanitized = stripUnsupportedPromptFields(initial);
            setFormValues(sanitized);
            formValuesRef.current = sanitized;
        } else {
            setFormValues(null);
            formValuesRef.current = null;
        }
    }, [fileContent, selectedFile]);

    const onFormChange = useCallback(
        (updated: Record<string, unknown>) => {
            const sanitized = stripUnsupportedPromptFields(updated);
            setFormValues(sanitized);
            formValuesRef.current = sanitized;
            const yamlStr = serializeYaml(sanitized);
            onEditorValueChange(yamlStr);
        },
        [onEditorValueChange],
    );

    const submitCreateTenant = async () => {
        if (!newTenantName.trim()) return;
        await onCreateTenant(newTenantName.trim(), fromBranch);
        setCreateModalOpen(false);
        setNewTenantName('');
        setFromBranch(undefined);
    };

    const openNewFileModal = useCallback((cat: PromptCategory) => {
        setNewFileCategory(cat);
        setNewFileId('');
        const tpl = JSON.parse(JSON.stringify(CATEGORY_TEMPLATES[cat]));
        setNewFileFormValues(tpl);
        setNewFileModalOpen(true);
    }, []);

    const submitCreateFile = async () => {
        if (!newFileId.trim() || !newFileFormValues) return;
        const finalValues = stripUnsupportedPromptFields({ ...newFileFormValues, id: newFileId.trim() });
        const template = serializeYaml(finalValues);
        await onCreateFile(newFileCategory, newFileId.trim(), template);
        setNewFileModalOpen(false);
        setNewFileId('');
        setNewFileFormValues(null);
    };

    const confirmDeleteFile = (category: PromptCategory, fileId: string) => {
        Modal.confirm({
            title: `Delete "${fileId}"?`,
            content: 'This will save the deletion immediately. You can revert from history.',
            okText: 'Delete',
            okType: 'danger',
            onOk: () => onDeleteFile(category, fileId),
        });
    };

    const confirmCloseDraft = () => {
        Modal.confirm({
            title: 'Delete saved changes and close draft?',
            content: 'This will close the current draft and delete saved changes that have not been published.',
            okText: 'Delete saved changes',
            okType: 'danger',
            cancelText: 'Keep editing',
            onOk: onCloseDraft,
        });
    };

    const renderFileTree = () => {
        if (!contentTree) return null;

        const searchLower = fileSearchText.toLowerCase();

        const items = CATEGORY_ORDER.filter((cat) => contentTree[cat]?.length > 0 || draftBranch).map((cat) => {
            const entries = (contentTree[cat] ?? []).filter(
                (entry: FileEntry) => !searchLower || entry.file_id.toLowerCase().includes(searchLower),
            );
            return {
            key: cat,
            label: (
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {CATEGORY_META[cat].icon}
                    <span style={{ marginLeft: 6, flex: 1 }}>{CATEGORY_META[cat].label}</span>
                    <Tag color="default" style={{ marginRight: 4 }}>
                        {entries.length}
                    </Tag>
                    {draftBranch && cat !== 'knowledge' && (
                        <Tooltip title={`New ${CATEGORY_META[cat].label.slice(0, -1)}`}>
                            <PlusOutlined
                                style={{ fontSize: 12, opacity: 0.6 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openNewFileModal(cat);
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            ),
            children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {entries.map((entry: FileEntry) => {
                        const isActive =
                            selectedFile?.category === cat &&
                            selectedFile?.fileId === entry.file_id;
                        const isDeleted = entry.draft_status === 'deleted';
                        const draftStatusMeta = entry.draft_status
                            ? {
                                added: { label: 'new', color: 'green' },
                                modified: { label: 'modified', color: 'orange' },
                                deleted: { label: 'deleted', color: 'red' },
                            }[entry.draft_status]
                            : null;
                        return (
                            <div
                                key={entry.file_id}
                                onClick={() => {
                                    if (!isDeleted) onSelectFile(cat, entry.file_id);
                                }}
                                style={{
                                    padding: '4px 8px',
                                    cursor: isDeleted ? 'not-allowed' : 'pointer',
                                    borderRadius: 4,
                                    background: isActive ? '#e6f4ff' : 'transparent',
                                    fontWeight: isActive ? 600 : 400,
                                    fontSize: 13,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    opacity: isDeleted ? 0.55 : 1,
                                }}
                            >
                                <FileTextOutlined style={{ fontSize: 12, opacity: 0.6 }} />
                                <span style={{ flex: 1, textDecoration: isDeleted ? 'line-through' : undefined }}>{entry.file_id}</span>
                                {draftStatusMeta && (
                                    <Tag color={draftStatusMeta.color} style={{ margin: 0, fontSize: 10 }}>
                                        {draftStatusMeta.label}
                                    </Tag>
                                )}
                                {draftBranch && !isDeleted && (
                                    <Tooltip title="Delete file">
                                        <DeleteOutlined
                                            style={{ fontSize: 11, opacity: 0, color: '#ff4d4f' }}
                                            className="file-delete-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDeleteFile(cat, entry.file_id);
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        );
                    })}
                </div>
            ),
        };
        });

        return (
            <Collapse
                defaultActiveKey={CATEGORY_ORDER}
                ghost
                size="small"
                items={items}
            />
        );
    };

    const renderPreview = () => {
        if (!fileContent) {
            return <Empty description="Select a file to preview" style={{ padding: 40 }} />;
        }
        const isMd = fileContent.filename.endsWith('.md');
        const liveText = editorValue || fileContent.raw;
        if (isMd) {
            return (
                <div ref={previewScrollRef} onScroll={handlePreviewScroll} style={{ padding: 16, overflow: 'auto', height: '100%' }}>
                    <SimpleMarkdown text={liveText} />
                </div>
            );
        }
        const previewData = formValues ?? fileContent.content;
        return (
            <div ref={previewScrollRef} onScroll={handlePreviewScroll} style={{ padding: 16, overflow: 'auto', height: '100%' }}>
                {renderValue(previewData)}
            </div>
        );
    };

    const renderHistoryTab = () => {
        if (historyLoading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="small" /></div>;
        if (!selectedFile) return <Empty description="Select a file first" style={{ padding: 40 }} />;
        if (history.length === 0) return <Empty description="No history" style={{ padding: 40 }} />;
        return (
            <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
                {history.length >= 2 && (
                    <div style={{ marginBottom: 12, padding: '6px 8px', background: '#fafafa', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Compare:</Text>
                        <Radio.Group
                            size="small"
                            value={compareFrom}
                            onChange={(e) => setCompareFrom(e.target.value)}
                        >
                            {history.map((h) => (
                                <Radio.Button key={h.sha} value={h.sha} style={{ fontSize: 11, padding: '0 6px' }}>
                                    {h.sha.slice(0, 7)}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                        <Text type="secondary" style={{ fontSize: 11 }}>→</Text>
                        <Radio.Group
                            size="small"
                            value={compareTo}
                            onChange={(e) => setCompareTo(e.target.value)}
                        >
                            {history.map((h) => (
                                <Radio.Button key={h.sha} value={h.sha} style={{ fontSize: 11, padding: '0 6px' }}>
                                    {h.sha.slice(0, 7)}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                        <Button
                            size="small"
                            type="primary"
                            disabled={!compareFrom || !compareTo || compareFrom === compareTo}
                            loading={versionDiffLoading}
                            onClick={() => compareFrom && compareTo && onLoadVersionDiff(compareFrom, compareTo)}
                        >
                            Compare
                        </Button>
                    </div>
                )}
                {versionDiff && compareFrom && compareTo && (
                    <div style={{ marginBottom: 12, border: '1px solid #e8e8e8', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ padding: '4px 8px', background: '#fafafa', borderBottom: '1px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                {compareFrom.slice(0, 7)} → {compareTo.slice(0, 7)}
                            </Text>
                            <Button size="small" type="text" onClick={() => { setCompareFrom(null); setCompareTo(null); }}>
                                Close
                            </Button>
                        </div>
                        <DiffView diff={versionDiff.diff} />
                    </div>
                )}
                <Timeline
                    items={history.map((h) => ({
                        children: (
                            <div style={{ fontSize: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text code style={{ fontSize: 11 }}>{h.sha.slice(0, 7)}</Text>
                                    <Tooltip title="Revert to this version">
                                        <Button
                                            size="small"
                                            type="text"
                                            icon={<UndoOutlined />}
                                            onClick={() => onRevert(h.sha)}
                                        />
                                    </Tooltip>
                                </div>
                                <div style={{ marginTop: 2 }}>
                                    <Text strong>{h.message}</Text>
                                </div>
                                <div>
                                    <Text type="secondary">{h.author}</Text>
                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                        {new Date(h.date).toLocaleString()}
                                    </Text>
                                </div>
                            </div>
                        ),
                    }))}
                />
            </div>
        );
    };

    const onRevertHunk = useCallback(
        (hunk: DiffHunk) => {
            const newLines = editorValue.split('\n');
            const removedLines = hunk.lines.filter((l) => l.type === 'removed').map((l) => l.text);
            const addedIndices = hunk.lines.filter((l) => l.type === 'added').map((l) => l.newIdx!);

            if (addedIndices.length > 0) {
                const sorted = [...addedIndices].sort((a, b) => b - a);
                sorted.forEach((idx) => { newLines.splice(idx, 1); });
            }
            if (removedLines.length > 0) {
                const insertAt = addedIndices.length > 0 ? Math.min(...addedIndices) : newLines.length;
                removedLines.forEach((line, i) => { newLines.splice(insertAt + i, 0, line); });
            }
            const newText = newLines.join('\n');
            onEditorValueChange(newText);
            try {
                const parsed = yaml.load(newText) as Record<string, unknown>;
                if (parsed && typeof parsed === 'object') {
                    setFormValues(parsed);
                    formValuesRef.current = parsed;
                }
            } catch { /* form stays as-is if parse fails */ }
        },
        [editorValue, onEditorValueChange],
    );

    const renderDiffTab = () => {
        if (!selectedFile) return <Empty description="Select a file first" style={{ padding: 40 }} />;

        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <LiveDiffView
                    savedText={savedContent}
                    draftText={editorValue}
                    onRevertHunk={onRevertHunk}
                />
                {diffData && (
                    <div style={{ borderTop: '1px solid #e8e8e8', marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 11, padding: '4px 8px', display: 'block' }}>
                            Git Diff (branch)
                        </Text>
                        <DiffView diff={diffData.diff} />
                    </div>
                )}
                {diffLoading && <div style={{ padding: 12, textAlign: 'center' }}><Spin size="small" /></div>}
            </div>
        );
    };

    const renderBlameTab = () => {
        if (blameLoading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="small" /></div>;
        if (!selectedFile) return <Empty description="Select a file first" style={{ padding: 40 }} />;
        if (blameData.length === 0) return <Empty description="No blame data" style={{ padding: 40 }} />;
        return (
            <pre style={{
                margin: 0,
                padding: 8,
                fontSize: 12,
                lineHeight: 1.7,
                fontFamily: "'SF Mono', 'Fira Code', monospace",
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}>
                {blameData.map((entry) => (
                    <div key={entry.line_number} style={{ display: 'flex', borderBottom: '1px solid #f5f5f5' }}>
                        <Tooltip title={`${entry.author} on ${new Date(entry.date).toLocaleString()}`}>
                            <span style={{ width: 80, flexShrink: 0, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {entry.sha.slice(0, 7)}
                            </span>
                        </Tooltip>
                        <span style={{ width: 90, flexShrink: 0, color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>
                            {entry.author}
                        </span>
                        <span style={{ flex: 1 }}>{entry.content}</span>
                    </div>
                ))}
            </pre>
        );
    };

    const onRightTabChange = (key: string) => {
        if (key === 'history' && selectedFile && history.length === 0) onLoadHistory();
        if (key === 'diff' && selectedFile && !diffData) onLoadDiff();
        if (key === 'blame' && selectedFile && blameData.length === 0) onLoadBlame();
    };

    const rightPanelTabs = [
        {
            key: 'preview',
            label: <span><EyeOutlined /> Preview</span>,
            children: renderPreview(),
        },
        {
            key: 'history',
            label: <span><HistoryOutlined /> History</span>,
            children: renderHistoryTab(),
        },
        {
            key: 'diff',
            label: <span><DiffOutlined /> Diff</span>,
            children: renderDiffTab(),
        },
        {
            key: 'blame',
            label: <span><TeamOutlined /> Blame</span>,
            children: renderBlameTab(),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>
            <style>{`
                div:hover > .file-delete-icon { opacity: 0.6 !important; }
                .file-delete-icon:hover { opacity: 1 !important; }
                .prompt-right-tabs .ant-tabs-content-holder { flex: 1; min-height: 0; overflow: auto; }
                .prompt-right-tabs .ant-tabs-content { height: 100%; }
                .prompt-right-tabs .ant-tabs-tabpane { height: 100%; overflow: auto; }
            `}</style>

            {/* Top bar — branch + actions */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 0',
                    marginBottom: 12,
                    borderBottom: '1px solid #f0f0f0',
                    flexWrap: 'wrap',
                }}
            >
                <Text strong>Branch:</Text>
                <Tooltip title="Choose the saved workspace/version of prompts to edit or review.">
                    <Select
                        placeholder="Select branch"
                        value={selectedTenant}
                        onChange={onSelectTenant}
                        loading={tenantsLoading}
                        style={{ minWidth: 220 }}
                        showSearch
                        options={tenants.map((t) => ({
                            value: t.name,
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {t.name}
                                    {t.drafts?.length > 0 && (
                                        <Badge
                                            color="orange"
                                            text={<span style={{ fontSize: 11, color: '#d48806' }}>draft</span>}
                                            style={{ marginLeft: 4 }}
                                        />
                                    )}
                                </span>
                            ),
                        }))}
                    />
                </Tooltip>
                <Tooltip title="Create a new branch to safely make prompt changes.">
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
                        New Branch
                    </Button>
                </Tooltip>

                <div style={{ flex: 1 }} />

                {draftBranch && (
                    <Tag color="blue" style={{ margin: 0 }}>
                        Draft: {draftBranch}
                    </Tag>
                )}

                <Space size="small">
                    <Popover
                        open={savePopoverOpen}
                        onOpenChange={(open) => { if (!open) { setSavePopoverOpen(false); setCommitMsg(''); } }}
                        trigger="click"
                        placement="bottomRight"
                        content={
                            <div style={{ width: 280 }}>
                                <Input
                                    placeholder="Change comment (optional)"
                                    value={commitMsg}
                                    onChange={(e) => setCommitMsg(e.target.value)}
                                    onPressEnter={() => {
                                        onSave(commitMsg || undefined);
                                        setSavePopoverOpen(false);
                                        setCommitMsg('');
                                    }}
                                    style={{ marginBottom: 8 }}
                                    autoFocus
                                />
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                                    <Button size="small" onClick={() => { setSavePopoverOpen(false); setCommitMsg(''); }}>Cancel</Button>
                                    <Button
                                        size="small"
                                        type="primary"
                                        loading={saving}
                                        onClick={() => {
                                            onSave(commitMsg || undefined);
                                            setSavePopoverOpen(false);
                                            setCommitMsg('');
                                        }}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        }
                    >
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            disabled={!isDirty || !selectedFile}
                            onClick={() => setSavePopoverOpen(true)}
                        >
                            Save
                        </Button>
                    </Popover>
                    <Button
                        icon={<CloudUploadOutlined />}
                        loading={publishing}
                        disabled={!draftBranch}
                        onClick={onPublish}
                        style={draftBranch ? { borderColor: '#1677ff', color: '#1677ff' } : undefined}
                    >
                        Publish
                    </Button>
                    {draftBranch && (
                        <Tooltip title="Delete saved changes">
                            <Button
                                icon={<CloseCircleOutlined />}
                                danger
                                onClick={confirmCloseDraft}
                            >
                                Delete saved changes
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            </div>

            {/* Three-panel layout */}
            <div style={{ display: 'flex', flex: 1, gap: 12, minHeight: 0 }}>
                {/* Left sidebar — file tree */}
                <div
                    style={{
                        width: 220,
                        minWidth: 180,
                        borderRight: '1px solid #f0f0f0',
                        overflowY: 'auto',
                        paddingRight: 8,
                        flexShrink: 0,
                    }}
                >
                    {!selectedTenant ? (
                        <div style={{ padding: 16, color: '#999', textAlign: 'center' }}>
                            Select a branch
                        </div>
                    ) : contentLoading ? (
                        <div style={{ padding: 24, textAlign: 'center' }}>
                            <Spin size="small" />
                        </div>
                    ) : (
                        <>
                            <Input
                                allowClear
                                placeholder="Filter files..."
                                size="small"
                                value={fileSearchText}
                                onChange={(e) => setFileSearchText(e.target.value)}
                                style={{ marginBottom: 8 }}
                            />
                            {renderFileTree()}
                        </>
                    )}
                </div>

                {/* Center — editor */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                    <div
                        style={{
                            padding: '4px 0 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <Title level={5} style={{ margin: 0 }}>
                            Editor
                        </Title>
                        {selectedFile && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {selectedFile.category}/{selectedFile.fileId}
                            </Text>
                        )}
                        {isDirty && <Tag color="orange">unsaved</Tag>}
                        <div style={{ flex: 1 }} />
                        {selectedFile && fileContent && !fileContent.filename.endsWith('.md') && (
                            <Tooltip title={editorMode === 'form' ? 'Switch to raw YAML' : 'Switch to form view'}>
                                <Button
                                    size="small"
                                    type="text"
                                    icon={editorMode === 'form' ? <CodeOutlined /> : <FormOutlined />}
                                    onClick={() => setEditorMode((m) => m === 'form' ? 'raw' : 'form')}
                                />
                            </Tooltip>
                        )}
                    </div>
                    {fileLoading ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Spin />
                        </div>
                    ) : !selectedFile ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#999',
                            }}
                        >
                            Select a file from the sidebar to edit
                        </div>
                    ) : fileContent && !fileContent.filename.endsWith('.md') && editorMode === 'form' && formValues ? (
                        <div
                            ref={editorScrollRef}
                            onScroll={handleEditorScroll}
                            style={{ flex: 1, overflowY: 'auto', padding: '4px 4px 4px 0' }}
                        >
                            <StructuredForm
                                content={formValues}
                                onChange={onFormChange}
                                category={selectedFile.category}
                                skillOptions={skillOptions}
                            />
                        </div>
                    ) : (
                        <div
                            ref={editorScrollRef}
                            onScroll={handleEditorScroll}
                            style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
                        >
                            <TextArea
                                value={editorValue}
                                onChange={(e) => onEditorValueChange(e.target.value)}
                                style={{
                                    fontFamily: "'SF Mono', 'Fira Code', monospace",
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                    resize: 'none',
                                    minHeight: '100%',
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Right — tabbed panel */}
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        borderLeft: '1px solid #f0f0f0',
                        paddingLeft: 12,
                    }}
                >
                    <Tabs
                        className="prompt-right-tabs"
                        defaultActiveKey="preview"
                        size="small"
                        items={rightPanelTabs}
                        onChange={onRightTabChange}
                        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                        tabBarStyle={{ marginBottom: 8, flexShrink: 0 }}
                    />
                </div>
            </div>

            {/* Create branch modal */}
            <Modal
                title="Create New Branch"
                open={createModalOpen}
                onOk={submitCreateTenant}
                onCancel={() => {
                    setCreateModalOpen(false);
                    setNewTenantName('');
                    setFromBranch(undefined);
                }}
                okText="Create"
                okButtonProps={{ disabled: !newTenantName.trim() }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <Text strong>Branch Name</Text>
                        <Input
                            placeholder="e.g. acme-rgm"
                            value={newTenantName}
                            onChange={(e) => setNewTenantName(e.target.value)}
                            style={{ marginTop: 4 }}
                        />
                        <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                            This will be created as:{' '}
                            <Text code>{branchNamePreview || `${branchNamePrefix}<branch-name>`}</Text>
                        </Text>
                    </div>
                    <div>
                        <Text strong>Clone From (optional)</Text>
                        <Select
                            placeholder="main"
                            value={fromBranch}
                            onChange={setFromBranch}
                            allowClear
                            style={{ width: '100%', marginTop: 4 }}
                            options={tenants.map((t) => ({ value: t.name, label: t.name }))}
                        />
                        <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                            Copy prompts from an existing branch as a starting point
                        </Text>
                    </div>
                </div>
            </Modal>

            {/* Create file modal */}
            <Modal
                title={`New ${CATEGORY_META[newFileCategory]?.label?.slice(0, -1) ?? 'File'}`}
                open={newFileModalOpen}
                onOk={submitCreateFile}
                onCancel={() => { setNewFileModalOpen(false); setNewFileId(''); setNewFileFormValues(null); }}
                okText="Create"
                okButtonProps={{ disabled: !newFileId.trim() }}
                width={600}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
                    <div>
                        <Text strong>File ID</Text>
                        <Input
                            placeholder="e.g. my_new_skill"
                            value={newFileId}
                            onChange={(e) => setNewFileId(e.target.value)}
                            style={{ marginTop: 4 }}
                        />
                        <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                            This becomes the filename (without extension).
                        </Text>
                    </div>
                    {newFileFormValues && (
                        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                            <StructuredForm
                                content={newFileFormValues}
                                onChange={(updated) => setNewFileFormValues(updated)}
                                category={newFileCategory}
                                skillOptions={skillOptions}
                                hiddenTopLevelFields={newFileCategory === 'skills' ? ['role', 'instructions'] : []}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default SkillStudioPromptsSection;
export type { SkillStudioPromptsSectionProps } from './SkillStudio.types';
